import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Label from '@/components/Label';

import useSettings from '@/hooks/useSettings';

import Page from '@/components/Page';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';

import { ROOTS_SETTINGS, settingsPath } from '../routes/paths';
import Iconify from '@/components/Iconify';
import {
  getEmailTemplate,
  getEmailTemplateStyle,
  previewEmailTemplate,
} from '../api';
import { SkeletonLoadingTableData } from '@/components/skeleton';
import {
  emailTemplateStyleToFormValues,
  formatEmailTemplateVariables,
  getEmailTemplateEffectiveSubject,
  getEmailTemplateEffectiveText,
  isEmailTemplateCustomized,
} from '../utils';
import { EmailTemplatePreviewPayload } from '../types';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

export default function ViewEmailTemplates() {
  const { id } = useParams();
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const allow = useGetAccess('settings');
  const { errorMsg } = useSnackbarMsg();
  const templateId = Number(id);

  const { data: template, isLoading } = useQuery({
    queryKey: ['emailTemplates', templateId],
    queryFn: () => getEmailTemplate(templateId),
    enabled: !!id,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching email template'),
  });

  const { data: emailStyle } = useQuery({
    queryKey: ['emailTemplateStyle'],
    queryFn: getEmailTemplateStyle,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching email style'),
  });

  const styleValues = emailTemplateStyleToFormValues(emailStyle);

  const previewPayload: EmailTemplatePreviewPayload | null = template
    ? {
        subject: template.subject,
        body_html: template.body_html ?? '',
        body_text: template.body_text ?? '',
        ...styleValues,
        ...(template.variables
          ? { preview_variables: template.variables }
          : {}),
      }
    : null;

  const {
    data: preview,
    isError: isPreviewError,
    isFetching: isPreviewFetching,
  } = useQuery({
    queryKey: ['emailTemplates', templateId, 'preview', previewPayload],
    queryFn: () =>
      previewEmailTemplate(
        templateId,
        previewPayload as EmailTemplatePreviewPayload
      ),
    enabled: !!template && !!previewPayload,
    refetchOnWindowFocus: false,
  });

  if (!id) {
    return <Navigate to={settingsPath('templates')} replace />;
  }

  const showSkeleton = isLoading || !template;
  const effectiveSubject = template
    ? getEmailTemplateEffectiveSubject(template)
    : '';
  const effectiveText = template ? getEmailTemplateEffectiveText(template) : '';
  const variables = template ? formatEmailTemplateVariables(template) : '';
  const isCustomized = template ? isEmailTemplateCustomized(template) : false;
  const liveHtmlContent = template ? template.body_html ?? '' : '';
  const defaultHtmlContent = template ? template.custom_body_html ?? '' : '';
  const previewSubject = preview?.subject ?? effectiveSubject;
  const previewDocument =
    preview?.body_html ?? '<p>Email preview loading...</p>';

  return (
    <Page title="Settings: View Email Template">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Email Template"
          links={[
            { name: 'Settings', href: ROOTS_SETTINGS },
            { name: 'Email Templates', href: settingsPath('templates') },
            { name: template?.name ?? String(id) },
          ]}
          action={
            allow.canUpdate && (
              <Button
                variant="contained"
                component={RouterLink}
                to={settingsPath('edit-template', 'id', String(id))}
                startIcon={<Iconify icon={'eva:edit-fill'} />}
              >
                Edit template
              </Button>
            )
          }
        />

        {showSkeleton ? (
          <SkeletonLoadingTableData />
        ) : (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h4">{template.name}</Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 0.5, color: 'text.secondary' }}
                      >
                        {template.ref}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Label
                        variant={
                          theme.palette.mode === 'light' ? 'ghost' : 'filled'
                        }
                        color={template.enabled ? 'success' : 'error'}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {template.enabled ? 'Enabled' : 'Disabled'}
                      </Label>
                      <Label
                        variant={
                          theme.palette.mode === 'light' ? 'ghost' : 'filled'
                        }
                        color={isCustomized ? 'info' : 'default'}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {isCustomized ? 'Customized' : 'Default'}
                      </Label>
                    </Stack>
                  </Stack>

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {template.description || 'No description'}
                  </Typography>
                  {template.template_id && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      OneSignal ID: {template.template_id}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Effective content</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subject"
                    value={effectiveSubject}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Plain-text body"
                    value={effectiveText}
                    multiline
                    minRows={5}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">
                    Live fields and default snapshots
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Live subject"
                    value={template.subject}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Live HTML content"
                    value={liveHtmlContent}
                    multiline
                    minRows={6}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Live plain-text body"
                    value={template.body_text ?? ''}
                    multiline
                    minRows={4}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Default subject snapshot"
                    value={template.custom_subject ?? ''}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Default HTML snapshot"
                    value={defaultHtmlContent}
                    multiline
                    minRows={6}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Default plain-text snapshot"
                    value={template.custom_body_text ?? ''}
                    multiline
                    minRows={4}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Available variables</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={variables || 'No variables configured'}
                    multiline
                    minRows={variables ? 6 : 2}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Email style</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Page background"
                      value={styleValues.page_background_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Card background"
                      value={styleValues.card_background_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Border color"
                      value={styleValues.border_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Text color"
                      value={styleValues.text_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Muted text color"
                      value={styleValues.muted_text_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Accent color"
                      value={styleValues.accent_color}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Button text color"
                      value={styleValues.button_text_color}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Logo"
                    value={styleValues.logo_enabled ? 'Enabled' : 'Disabled'}
                    InputProps={{ readOnly: true }}
                  />
                  {styleValues.logo_enabled && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Logo URL"
                      value={styleValues.logo_url}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Preview</Typography>
                  <Typography variant="subtitle2">{previewSubject}</Typography>
                  {isPreviewFetching && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      Updating preview...
                    </Typography>
                  )}
                  {isPreviewError && (
                    <Typography variant="caption" sx={{ color: 'error.main' }}>
                      Preview unavailable
                    </Typography>
                  )}
                  <Divider />
                  <Box
                    component="iframe"
                    title="Email preview"
                    sandbox=""
                    srcDoc={previewDocument}
                    sx={{
                      width: 1,
                      minHeight: 420,
                      border: 0,
                      bgcolor: 'background.neutral',
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
